import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { useVideos } from '../contexts/VideoContext';
import VideoManager from './VideoManager';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { ArrowLeft, CheckCircle2, Share2 } from 'lucide-react';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: (() => void) | undefined;
  }
}

interface VideoPlayerPageProps {
  currentView?: string; // formato: player:<contentId>[:<videoId>]
  onViewChange?: (view: string) => void;
  onBack?: () => void;
}

function parseView(view?: string): { contentId?: string; videoId?: string } {
  if (!view) return {};
  if (!view.startsWith('player:')) return {};
  const [, contentId, videoId] = view.split(':');
  return { contentId, videoId };
}

const VideoPlayerPage: React.FC<VideoPlayerPageProps> = ({ currentView, onViewChange, onBack }) => {
  const { user, isAdmin } = useAuth();
  const { showToast } = useToast();
  const { videosByContentId, loadContentVideos } = useVideos();

  const { contentId, videoId } = useMemo(() => parseView(currentView), [currentView]);
  const list = (contentId && videosByContentId[contentId]) || [];

  const [showManager, setShowManager] = useState(false);
  const [completedVideoIds, setCompletedVideoIds] = useState<string[]>([]);
  const [contentInfo, setContentInfo] = useState<{ title?: string; description?: string } | null>(null);

  // Carregar detalhes do conteúdo pai (para fallback de descrição)
  useEffect(() => {
    if (!contentId) return;
    const fetchContentInfo = async () => {
      try {
        const { data } = await supabase
          .from('content_items')
          .select('title, description')
          .eq('id', contentId)
          .maybeSingle();
        if (data) setContentInfo(data);
      } catch (err) {
        console.error('Error fetching content info:', err);
      }
    };
    fetchContentInfo();
  }, [contentId]);

  // Carregar progresso do usuário para os vídeos deste conteúdo
  const loadUserProgress = useCallback(async () => {
    if (!user || !contentId) return;
    try {
      const { data, error } = await supabase
        .from('user_video_progress')
        .select('video_id')
        .eq('content_id', contentId)
        .eq('user_id', user.id)
        .eq('is_completed', true);
      
      if (!error && data) {
        setCompletedVideoIds(data.map(row => row.video_id));
      }
    } catch (err) {
      console.error('Error loading progress:', err);
    }
  }, [user?.id, contentId]);

  useEffect(() => {
    if (contentId && user?.id) {
      loadUserProgress();
    }
  }, [contentId, user?.id, loadUserProgress]);

  useEffect(() => {
    if (contentId) {
      loadContentVideos(contentId);
    }
  }, [contentId, loadContentVideos]);

  const activeVideo = useMemo(() => {
    if (!list.length) return undefined;
    if (videoId) {
      return list.find(v => v.id === videoId || v.youtube_video_id === videoId) || list[0];
    }
    return list[0];
  }, [list, videoId]);

  // Atualizar Meta Tags do Open Graph e Título da Página dinamicamente
  useEffect(() => {
    if (activeVideo) {
      document.title = `${activeVideo.title} | MasterClub`;
      const videoThumb = activeVideo.thumbnail_url || `https://img.youtube.com/vi/${activeVideo.youtube_video_id}/hqdefault.jpg`;
      
      const setMetaTag = (property: string, content: string) => {
        let element = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
        if (!element) {
          element = document.createElement('meta');
          if (property.startsWith('og:')) {
            element.setAttribute('property', property);
          } else {
            element.setAttribute('name', property);
          }
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      setMetaTag('og:title', activeVideo.title);
      setMetaTag('og:description', activeVideo.description || contentInfo?.description || 'Assista a esta aula na MasterClub!');
      setMetaTag('og:image', videoThumb);
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', activeVideo.title);
      setMetaTag('twitter:image', videoThumb);
    }
  }, [activeVideo, contentInfo]);

  // Atualizar URL da barra do navegador com o id do conteúdo e id do vídeo ativo (parâmetros concisos c e v)
  useEffect(() => {
    if (contentId && activeVideo) {
      const videoIdentifier = activeVideo.youtube_video_id || activeVideo.id;
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('c', contentId);
      currentUrl.searchParams.set('v', videoIdentifier);
      currentUrl.searchParams.delete('aula');
      currentUrl.searchParams.delete('video');
      window.history.replaceState(null, '', currentUrl.toString());
    }
  }, [contentId, activeVideo?.id, activeVideo?.youtube_video_id]);

  const setActiveVideo = (id: string) => {
    if (!contentId || !onViewChange) return;
    onViewChange(`player:${contentId}:${id}`);
  };

  // Copiar link da aula para a área de transferência ou acionar compartilhamento nativo
  const handleShareLesson = async () => {
    if (!contentId || !activeVideo) return;

    const videoIdentifier = activeVideo.youtube_video_id || activeVideo.id;
    const shareUrl = `${window.location.origin}${window.location.pathname}?c=${encodeURIComponent(contentId)}&v=${encodeURIComponent(videoIdentifier)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: activeVideo.title,
          text: `Assista a aula "${activeVideo.title}" na plataforma MasterClub!`,
          url: shareUrl,
        });
        showToast({
          type: 'success',
          title: 'Compartilhado!',
          message: 'Aula compartilhada com sucesso.',
        });
        return;
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast({
        type: 'success',
        title: 'Link copiado!',
        message: 'O link desta aula foi copiado para a sua área de transferência.',
      });
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Erro ao copiar',
        message: 'Não foi possível copiar o link da aula.',
      });
    }
  };

  // Salvar progresso de conclusão no banco
  const handleMarkVideoAsCompleted = async (targetVideoId: string, isCompleted: boolean = true) => {
    if (!user) {
      showToast({
        type: 'info',
        title: 'Acesso restrito',
        message: 'Você precisa estar logado na plataforma para acompanhar o seu progresso.'
      });
      return;
    }

    if (!contentId) return;

    try {
      const { error } = await supabase
        .from('user_video_progress')
        .upsert({
          user_id: user.id,
          video_id: targetVideoId,
          content_id: contentId,
          watched_percentage: isCompleted ? 100 : 0,
          is_completed: isCompleted,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,video_id'
        });

      if (error) {
        console.error('Error saving video progress:', error);
      } else {
        // Atualizar estado local
        setCompletedVideoIds(prev => 
          isCompleted 
            ? [...prev.filter(id => id !== targetVideoId), targetVideoId]
            : prev.filter(id => id !== targetVideoId)
        );
      }
    } catch (err) {
      console.error('Error in handleMarkVideoAsCompleted:', err);
    }
  };

  // Carregar YouTube Player API e monitorar reprodução
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }
  }, []);

  useEffect(() => {
    let player: any;
    let intervalId: any;

    const onPlayerStateChange = (event: any) => {
      // event.data === 1 significa PLAYING (reproduzindo)
      if (event.data === 1) {
        intervalId = setInterval(() => {
          if (player && typeof player.getDuration === 'function' && typeof player.getCurrentTime === 'function') {
            const duration = player.getDuration();
            const currentTime = player.getCurrentTime();
            if (duration > 0 && activeVideo) {
              const pct = (currentTime / duration) * 100;
              if (pct >= 70) {
                // Marcar como concluído automaticamente ao bater 70%
                handleMarkVideoAsCompleted(activeVideo.id, true);
                clearInterval(intervalId);
              }
            }
          }
        }, 3000);
      } else {
        if (intervalId) clearInterval(intervalId);
      }
    };

    const initPlayer = () => {
      if (window.YT && window.YT.Player && activeVideo) {
        player = new window.YT.Player(`yt-player-${activeVideo.id}`, {
          events: {
            onStateChange: onPlayerStateChange
          }
        });
      }
    };

    if (activeVideo) {
      if (window.YT && window.YT.Player) {
        const timer = setTimeout(initPlayer, 800);
        return () => {
          clearTimeout(timer);
          if (intervalId) clearInterval(intervalId);
          if (player && typeof player.destroy === 'function') {
            try {
              player.destroy();
            } catch {}
          }
        };
      } else {
        const previousCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (previousCallback) previousCallback();
          initPlayer();
        };
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeVideo]);

  if (!contentId) {
    return (
      <div className="flex-1 bg-[#121316] min-h-screen pt-24 text-center text-white">Nenhum conteúdo selecionado.</div>
    );
  }

  return (
    <div className="flex-1 bg-[#121316] min-h-screen pt-24">
      <div className="max-w-[1600px] mx-auto px-4 pb-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={onBack} 
            variant="outline" 
            className="flex items-center gap-2 text-gray-300 border-gray-700 hover:text-[#0FFF55] hover:border-[#0FFF55] hover:bg-[#0FFF55]/10 rounded-xl px-4 py-2 transition-all duration-300"
          >
            <ArrowLeft size={16} />
            Voltar
          </Button>
          {isAdmin && (
            <Button onClick={() => setShowManager(true)} className="bg-[#0FFF55] text-black hover:bg-[#0dd149] rounded-xl px-4 py-2">Gerenciar vídeos</Button>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Player principal */}
          <div className="xl:col-span-8">
            <div className="w-full aspect-video bg-black/40 backdrop-blur-md rounded-3xl overflow-hidden border border-white/10 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]">
              {activeVideo ? (
                <iframe
                  id={`yt-player-${activeVideo.id}`}
                  key={activeVideo.id}
                  src={`https://www.youtube.com/embed/${activeVideo.youtube_video_id}?enablejsapi=1&rel=0`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">Nenhum vídeo disponível</div>
              )}
            </div>

            <div className="mt-6 bg-[#1a1d21]/40 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h1 className="text-white text-2xl font-bold">{activeVideo?.title || '—'}</h1>
                {activeVideo && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={handleShareLesson}
                      className="flex items-center gap-2 border border-gray-700 bg-transparent text-gray-300 hover:text-[#0FFF55] hover:border-[#0FFF55]/40 hover:bg-[#0FFF55]/10 px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300 shadow-sm"
                      title="Compartilhar link desta aula"
                    >
                      <Share2 size={14} className="text-gray-400 group-hover:text-[#0FFF55]" />
                      Compartilhar Aula
                    </button>

                    <button
                      onClick={() => handleMarkVideoAsCompleted(activeVideo.id, !completedVideoIds.includes(activeVideo.id))}
                      className={`flex items-center gap-2 border px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300 ${
                        completedVideoIds.includes(activeVideo.id)
                          ? 'bg-[#0FFF55]/10 border-[#0FFF55]/30 text-[#0FFF55] hover:bg-[#0FFF55]/20 hover:border-[#0FFF55]/50'
                          : 'bg-transparent border-gray-700 text-gray-400 hover:text-[#0FFF55] hover:border-[#0FFF55]/40 hover:bg-[#0FFF55]/5'
                      }`}
                    >
                      <CheckCircle2 size={14} className={completedVideoIds.includes(activeVideo.id) ? "fill-[#0FFF55]/10 text-[#0FFF55]" : ""} />
                      {completedVideoIds.includes(activeVideo.id) ? 'Concluído' : 'Marcar como Concluído'}
                    </button>
                  </div>
                )}
              </div>
              {(activeVideo?.description?.trim() || contentInfo?.description?.trim()) && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Sobre esta aula</h3>
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {activeVideo?.description?.trim() || contentInfo?.description?.trim()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Playlist lateral */}
          <div className="xl:col-span-4">
            <div className="bg-[#1a1d21]/60 backdrop-blur-md rounded-3xl border border-white/10 p-4 max-h-[70vh] overflow-y-auto shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]">
              <div className="space-y-3">
                {list.map(v => (
                  <div
                    key={v.id}
                    className={`w-full flex items-center gap-3 rounded-2xl p-2 transition duration-300 border ${
                      v.id === activeVideo?.id
                        ? 'bg-[#0FFF55]/10 border-[#0FFF55]/20 text-[#0FFF55]'
                        : 'hover:bg-white/5 border-transparent text-white'
                    }`}
                  >
                    {/* Botão de seleção do vídeo */}
                    <button
                      onClick={() => setActiveVideo(v.id)}
                      className="flex-1 flex gap-3 text-left min-w-0"
                    >
                      <div className="relative w-32 h-20 flex-shrink-0">
                        <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover rounded-xl border border-white/5" />
                        {completedVideoIds.includes(v.id) && (
                          <div className="absolute top-1 right-1 bg-black/85 backdrop-blur-sm rounded-full p-1 border border-[#0FFF55]/30">
                            <CheckCircle2 size={14} className="text-[#0FFF55] fill-[#0FFF55]/10" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="text-sm font-semibold line-clamp-2">{v.title}</div>
                        {v.description && (
                          <div className="text-gray-400 text-xs line-clamp-2 mt-1">{v.description}</div>
                        )}
                      </div>
                    </button>

                    {/* Botão de conclusão à direita */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkVideoAsCompleted(v.id, !completedVideoIds.includes(v.id));
                      }}
                      className={`flex-shrink-0 w-10 h-20 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        completedVideoIds.includes(v.id)
                          ? 'text-[#0FFF55] hover:text-red-400 bg-[#0FFF55]/5 hover:bg-red-500/10'
                          : 'text-gray-500 hover:text-[#0FFF55] hover:bg-white/5'
                      }`}
                      title={completedVideoIds.includes(v.id) ? "Desmarcar como assistido" : "Marcar como assistido"}
                    >
                      <CheckCircle2 
                        size={20} 
                        className={`transition-all duration-300 ${completedVideoIds.includes(v.id) ? 'fill-[#0FFF55]/10 scale-110' : 'opacity-40'}`} 
                      />
                    </button>
                  </div>
                ))}
                {!list.length && (
                  <div className="text-gray-400 text-sm p-2">Nenhum vídeo nesta playlist ainda.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {contentId && (
        <VideoManager contentId={contentId} open={showManager} onClose={() => setShowManager(false)} />
      )}
    </div>
  );
};

export default VideoPlayerPage;


